const API_BASE = "/api/v1";

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  mfa_required?: boolean;
  mfa_challenge_token?: string | null;
}

const ACCESS_KEY = "ws.access";
const REFRESH_KEY = "ws.refresh";

export const tokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(pair: TokenPair): void {
    if (pair.access_token) localStorage.setItem(ACCESS_KEY, pair.access_token);
    if (pair.refresh_token) localStorage.setItem(REFRESH_KEY, pair.refresh_token);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Render the backend's error payload as a single human-readable string.
 * Handles:
 *   - {detail: "string"}
 *   - {detail: [{type, loc, msg, ...}, ...]}  ← FastAPI 422 validation
 *   - raw string body
 *   - anything else → fall back to HTTP code
 */
function formatErrorMessage(detail: unknown, httpStatus: number): string {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    const d = (detail as { detail?: unknown }).detail;
    if (typeof d === "string" && d.trim()) return d;
    if (Array.isArray(d)) {
      const parts = d.map((entry) => {
        if (entry && typeof entry === "object") {
          const e = entry as { loc?: unknown; msg?: string; type?: string };
          const loc = Array.isArray(e.loc) ? e.loc.filter((s) => s !== "body").join(".") : "";
          return `${loc ? `${loc}: ` : ""}${e.msg ?? e.type ?? JSON.stringify(entry)}`;
        }
        return String(entry);
      });
      if (parts.length) return parts.join("; ");
    }
  }
  return `HTTP ${httpStatus}`;
}

async function refreshAccess(): Promise<boolean> {
  if (!tokens.refresh) return false;
  const resp = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: tokens.refresh }),
  });
  if (!resp.ok) {
    tokens.clear();
    return false;
  }
  const data: TokenPair = await resp.json();
  tokens.set(data);
  return true;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  opts: { retriedAuth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (tokens.access) headers.set("Authorization", `Bearer ${tokens.access}`);

  const resp = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (resp.status === 401 && !opts.retriedAuth && tokens.refresh) {
    const ok = await refreshAccess();
    if (ok) return apiFetch<T>(path, init, { retriedAuth: true });
  }

  if (!resp.ok) {
    const text = await resp.text();
    let detail: unknown = text;
    try {
      detail = JSON.parse(text);
    } catch {
      /* leave as raw text */
    }
    throw new ApiError(resp.status, detail, formatErrorMessage(detail, resp.status));
  }

  if (resp.status === 204) return undefined as T;

  const ct = resp.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return (await resp.json()) as T;
  return (await resp.text()) as unknown as T;
}
